import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Building2, Briefcase } from 'lucide-react';
import emailjs from '@emailjs/browser';

interface RegistrationFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackage?: string | null;
}

const packages = {
  modular: { name: 'Zero2Export™ Modular', price: '80000' },
  full: { name: 'Zero2Export™', price: '160000' },
  plus: { name: 'Zero2Export™ + BCEM', price: '310000' }
};

const RegistrationForm: React.FC<RegistrationFormProps> = ({ isOpen, onClose, selectedPackage }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    position: '',
    businessType: '',
    yearsInBusiness: '',
    exportExperience: '',
    heardFrom: '',
    expectations: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [paystackReady, setPaystackReady] = useState(false);

  useEffect(() => {
    // Function to check if Paystack is loaded
    const checkPaystack = () => {
      if (window.PaystackPop) {
        setPaystackReady(true);
        return true;
      }
      return false;
    };

    // First check if already loaded
    if (checkPaystack()) return;

    // If not loaded, create script tag
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    
    script.onload = () => {
      if (checkPaystack()) {
        console.log('Paystack loaded successfully');
      } else {
        console.error('Paystack script loaded but PaystackPop not available');
        setSubmitError('Payment processor failed to load. Please refresh the page.');
      }
    };

    script.onerror = () => {
      console.error('Failed to load Paystack script');
      setSubmitError('Failed to load payment processor. Please refresh the page.');
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (!isOpen) return null;

  const selectedPkg = selectedPackage ? packages[selectedPackage as keyof typeof packages] : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPkg) {
      setSubmitError('Please select a package');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Submit form data via EmailJS
      const emailJsResponse = await emailjs.send(
        'service_8tju96a',
        'template_pqrwfpb',
        {
          ...formData,
          package: selectedPkg.name,
          price: selectedPkg.price,
        },
        'rmSymC-Nd1L1JR5Mk'
      );

      console.log('EmailJS success:', emailJsResponse);

      // Verify Paystack is ready
      if (!window.PaystackPop) {
        throw new Error('Payment processor not ready. Please wait a moment and try again.');
      }

      // Initialize Paystack payment
      const handler = window.PaystackPop.setup({
        key: 'pk_live_8a4c486aa818a4bb95c24504316341f54e6c4e91',
        email: formData.email,
        amount: parseInt(selectedPkg.price) * 100,
        currency: 'NGN',
        metadata: {
          custom_fields: [
            {
              display_name: "Full Name",
              variable_name: "full_name",
              value: formData.fullName
            },
            {
              display_name: "Package",
              variable_name: "package",
              value: selectedPkg.name
            }
          ]
        },
        callback: function(response: any) {
          console.log('Payment successful:', response);
          onClose();
        },
        onClose: function() {
          console.log('Payment window closed');
        }
      });

      handler.openIframe();
    } catch (error: any) {
      console.error('Error:', error);
      setSubmitError(error.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg font-medium text-gray-900">Register for Zero2Export™</h3>
              
              {selectedPkg && (
                <div className="mt-2 bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">Selected Package</p>
                  <p className="font-semibold text-gray-900">{selectedPkg.name}</p>
                  <p className="text-blue-600 font-medium">₦{parseInt(selectedPkg.price).toLocaleString()}</p>
                </div>
              )}

              {submitError && (
                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <div className="mt-1 relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="mt-1 relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <div className="mt-1 relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="tel"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Organization</label>
                    <div className="mt-1 relative">
                      <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Position</label>
                    <div className="mt-1 relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Business Type</label>
                    <select
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.businessType}
                      onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    >
                      <option value="">Select Business Type</option>
                      <option value="startup">Startup</option>
                      <option value="sme">SME</option>
                      <option value="corporate">Corporate</option>
                      <option value="individual">Individual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Years in Business</label>
                    <select
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.yearsInBusiness}
                      onChange={(e) => setFormData({ ...formData, yearsInBusiness: e.target.value })}
                    >
                      <option value="">Select Experience</option>
                      <option value="0">Just Starting</option>
                      <option value="1-2">1-2 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="5+">5+ years</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Export Experience</label>
                    <select
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.exportExperience}
                      onChange={(e) => setFormData({ ...formData, exportExperience: e.target.value })}
                    >
                      <option value="">Select Export Experience</option>
                      <option value="none">No Experience</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">How did you hear about us?</label>
                                        <select
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.heardFrom}
                      onChange={(e) => setFormData({ ...formData, heardFrom: e.target.value })}
                    >
                      <option value="">Select an option</option>
                      <option value="social_media">Social Media</option>
                      <option value="email">Email</option>
                      <option value="referral">Referral</option>
                      <option value="event">Event</option>
                      <option value="search_engine">Search Engine</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">What do you hope to gain from this program?</label>
                    <textarea
                      required
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formData.expectations}
                      onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {isSubmitting ? 'Submitting...' : 'Register and Pay'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
